import React, { useState, useEffect } from 'react';
import { 
  X, 
  User, 
  Mail, 
  Calendar, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Ban,
  UserCheck,
  UserX,
  FileText,
  Activity,
  ExternalLink,
  Eye,
  Trash2
} from 'lucide-react';
import { supabase } from '../../utils/supabase';
import { useAuth } from '../../contexts/AuthContext';
import DeleteUserConfirmationDialog from './DeleteUserConfirmationDialog';
import SuspensionModal from './SuspensionModal';
import { identityVerificationService } from '../../services/identityVerificationService';

const UserDetailsModal = ({ user, isOpen, onClose, onAction }) => {
  const { userProfile } = useAuth();
  const [actionNote, setActionNote] = useState('');
  const [reports, setReports] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [reportsError, setReportsError] = useState(null);
  const [activitiesError, setActivitiesError] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuspensionModal, setShowSuspensionModal] = useState(false);
  const [showQRPreview, setShowQRPreview] = useState(false);
  const [verificationNote, setVerificationNote] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [suspensions, setSuspensions] = useState([]);
  const [loadingModeration, setLoadingModeration] = useState(false);

  // Fetch warnings and suspensions for this user
  const fetchModerationHistory = async (userId) => {
    setLoadingModeration(true);
    try {
      const { data: adminActions, error } = await supabase
        .from('admin_actions')
        .select(`
          id,
          action_type,
          created_at,
          reason,
          notes,
          admin:user_profiles!admin_actions_admin_user_id_fkey(full_name, email)
        `)
        .eq('target_user_id', userId)
        .in('action_type', ['user_warned', 'user_suspended'])
        .order('created_at', { ascending: false });

      if (error) throw error;

      const warningsList = adminActions?.filter(a => a.action_type === 'user_warned') || [];
      const suspensionsList = adminActions?.filter(a => a.action_type === 'user_suspended') || [];

      setWarnings(warningsList);
      setSuspensions(suspensionsList);
    } catch (error) {
      console.error('Error fetching moderation history:', error);
    } finally {
      setLoadingModeration(false);
    }
  };

  // Fetch reports related to this user
  const fetchReports = async (userId) => {
    setLoadingReports(true);
    setReportsError(null);
    try {
      const { data, error } = await supabase
        .from('reports')
        .select(`
          id,
          type,
          reason,
          description,
          status,
          priority,
          created_at,
          resolved_at,
          reporter:user_profiles!reports_reported_by_fkey(full_name, email),
          resolver:user_profiles!reports_resolved_by_fkey(full_name, email)
        `)
        .eq('reported_user', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReportsError('Failed to load reports');
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  // Fetch recent activities related to this user
  const fetchActivities = async (userId, userRole) => {
    setLoadingActivities(true);
    setActivitiesError(null);
    try {
      let activities = [];
      
      // Fetch admin actions targeting this user
      const { data: adminActions, error: adminError } = await supabase
        .from('admin_actions')
        .select(`
          id,
          action_type,
          created_at,
          reason,
          notes,
          admin:user_profiles!admin_actions_admin_user_id_fkey(full_name, email)
        `)
        .eq('target_user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (adminError) throw adminError;
      
      // Add admin actions to activities
      if (adminActions) {
        activities = [...activities, ...adminActions.map(action => ({
          id: `admin-${action.id}`,
          type: 'admin_action',
          title: formatActionType(action.action_type),
          description: action.notes || action.reason || 'Admin action performed',
          timestamp: action.created_at,
          metadata: {
            admin: action.admin?.full_name || 'System',
            actionType: action.action_type
          }
        }))];
      }

      // Fetch user's own activities based on role
      const activityTable = userRole === 'employer' ? 'employer_activities' : 'va_activities';
      const userIdField = userRole === 'employer' ? 'employer_id' : 'va_id';
      
      const { data: userActivities, error: userError } = await supabase
        .from(activityTable)
        .select('*')
        .eq(userIdField, userId)
        .order(userRole === 'employer' ? 'activity_date' : 'timestamp', { ascending: false })
        .limit(15);

      if (userError) {
        // If table doesn't exist or other error, just continue with admin actions
        console.warn(`Could not fetch ${activityTable}:`, userError);
      } else if (userActivities) {
        // Add user activities
        activities = [...activities, ...userActivities.map(activity => ({
          id: `user-${activity.id}`,
          type: 'user_activity',
          title: activity.title || formatActivityType(activity.type || activity.activity_type),
          description: activity.description || 'User activity',
          timestamp: activity.timestamp || activity.activity_date,
          metadata: activity.metadata || {}
        }))];
      }

      // Sort all activities by timestamp
      activities.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setActivities(activities.slice(0, 20)); // Limit to 20 most recent
    } catch (error) {
      console.error('Error fetching activities:', error);
      setActivitiesError('Failed to load activities');
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  // Format action type for display
  const formatActionType = (actionType) => {
    return actionType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown Action';
  };

  // Format activity type for display
  const formatActivityType = (activityType) => {
    const typeMap = {
      'job_posted': 'Job Posted',
      'application_received': 'Application Received',
      'interview_scheduled': 'Interview Scheduled',
      'message_sent': 'Message Sent',
      'application': 'Job Application',
      'match': 'Job Match',
      'profile_view': 'Profile Viewed',
      'skill_assessment': 'Skill Assessment',
      'interview': 'Interview',
      'message': 'Message',
      'recommendation': 'Job Recommendation'
    };
    return typeMap[activityType] || (activityType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Activity');
  };

  // Format timestamp to relative time
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return activityTime.toLocaleDateString();
  };

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && user?.id) {
      fetchReports(user.id);
      fetchActivities(user.id, user.role);
      fetchModerationHistory(user.id);
    }
  }, [isOpen, user?.id, user?.role]);

  if (!isOpen || !user) return null;

  const handleAction = (action) => {
    if (action === 'suspend') {
      setShowSuspensionModal(true);
    } else {
      onAction(user.id, action, actionNote);
      setActionNote('');
      onClose();
    }
  };

  const handleSuspensionConfirm = async (reason, duration) => {
    const result = await onAction(user.id, 'suspend', reason, duration);
    setShowSuspensionModal(false);
    setActionNote('');
    if (result?.success) {
      onClose();
    }
  };

  const handleDeleteConfirm = async (reason) => {
    const result = await onAction(user.id, 'delete', reason);
    setShowDeleteConfirmation(false);
    if (result?.success) {
      onClose();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50 border-green-200';
      case 'warned': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'suspended': return 'text-red-600 bg-red-50 border-red-200';
      case 'banned': return 'text-red-800 bg-red-100 border-red-300';
      case 'pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'va': return 'text-blue-600 bg-blue-50';
      case 'employer': return 'text-purple-600 bg-purple-50';
      case 'admin': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-gray-500" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{user.full_name || user.name}</h2>
              <p className="text-gray-600 text-sm">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Status and Role */}
          <div className="flex items-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(user.moderation_status?.status || 'active')}`}>
              {(user.moderation_status?.status || 'active') === 'active' && <CheckCircle className="w-4 h-4 inline mr-1" />}
              {(user.moderation_status?.status || 'active') === 'warned' && <AlertTriangle className="w-4 h-4 inline mr-1" />}
              {(user.moderation_status?.status || 'active') === 'suspended' && <Ban className="w-4 h-4 inline mr-1" />}
              {(user.moderation_status?.status || 'active') === 'banned' && <Ban className="w-4 h-4 inline mr-1" />}
              {(user.moderation_status?.status || 'active') === 'pending' && <Clock className="w-4 h-4 inline mr-1" />}
              {(user.moderation_status?.status || 'active').toUpperCase()}
            </span>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(user.role)}`}>
              <Shield className="w-4 h-4 inline mr-1" />
              {(user.role || 'user').toUpperCase()}
            </span>
          </div>

          {/* User Information Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>
              
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-gray-900">{user.email}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Join Date</p>
                    <p className="text-gray-900">{user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Clock className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="text-gray-900">{user.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 border-b pb-2">User Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-blue-600 text-sm font-medium">Role</p>
                  <p className="text-2xl font-bold text-blue-700">{(user.role || 'user').toUpperCase()}</p>
                </div>
                
                <div className="bg-green-50 p-3 rounded-lg">
                  <p className="text-green-600 text-sm font-medium">Status</p>
                  <p className="text-2xl font-bold text-green-700">{(user.moderation_status?.status || 'active').toUpperCase()}</p>
                </div>
                
                <div className={`p-3 rounded-lg ${
                  user.identity_verification_status === 'verified' ? 'bg-green-50' :
                  user.identity_verification_status === 'pending' ? 'bg-amber-50' :
                  user.identity_verification_status === 'rejected' ? 'bg-red-50' : 'bg-purple-50'
                }`}>
                  <p className={`text-sm font-medium ${
                    user.identity_verification_status === 'verified' ? 'text-green-600' :
                    user.identity_verification_status === 'pending' ? 'text-amber-600' :
                    user.identity_verification_status === 'rejected' ? 'text-red-600' : 'text-purple-600'
                  }`}>Identity Verified</p>
                  <p className={`text-2xl font-bold ${
                    user.identity_verification_status === 'verified' ? 'text-green-700' :
                    user.identity_verification_status === 'pending' ? 'text-amber-700' :
                    user.identity_verification_status === 'rejected' ? 'text-red-700' : 'text-purple-700'
                  }`}>
                    {(user.identity_verification_status || 'unverified').toUpperCase()}
                  </p>
                </div>
                
                <div className="bg-yellow-50 p-3 rounded-lg">
                  <p className="text-yellow-600 text-sm font-medium">Location</p>
                  <p className="text-lg font-bold text-yellow-700">{user.location || 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Identity Verification Section (VA users only) */}
          {user.role === 'va' && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-purple-600" />
                Identity Verification
              </h3>

              {user.identity_verification_status === 'pending' && user.identity_verification_qr_link ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-amber-700 bg-amber-50 p-3 rounded-lg">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Pending Review — VA submitted a QR code link</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <a
                      href={user.identity_verification_qr_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      View QR Image
                    </a>
                    <button
                      onClick={() => setShowQRPreview(true)}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      Preview
                    </button>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Note (optional)</label>
                    <textarea
                      value={verificationNote}
                      onChange={(e) => setVerificationNote(e.target.value)}
                      placeholder="Add a note about your decision..."
                      className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={2}
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      disabled={verificationLoading}
                      onClick={async () => {
                        setVerificationLoading(true);
                        try {
                          await identityVerificationService.reviewVerification(
                            user.id, userProfile.id, 'approve', verificationNote
                          );
                          setVerificationNote('');
                          if (onAction) onAction();
                        } catch (err) {
                          console.error('Verification approve error:', err);
                        } finally {
                          setVerificationLoading(false);
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Approve
                    </button>
                    <button
                      disabled={verificationLoading}
                      onClick={async () => {
                        setVerificationLoading(true);
                        try {
                          await identityVerificationService.reviewVerification(
                            user.id, userProfile.id, 'reject', verificationNote
                          );
                          setVerificationNote('');
                          if (onAction) onAction();
                        } catch (err) {
                          console.error('Verification reject error:', err);
                        } finally {
                          setVerificationLoading(false);
                        }
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      <Ban className="w-4 h-4" />
                      Reject
                    </button>
                  </div>
                </div>
              ) : user.identity_verification_status === 'verified' ? (
                <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Verified</p>
                    {user.identity_verified_at && (
                      <p className="text-xs text-green-600">Verified on {new Date(user.identity_verified_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              ) : user.identity_verification_status === 'rejected' ? (
                <div className="flex items-center gap-2 text-red-700 bg-red-50 p-3 rounded-lg">
                  <Ban className="w-5 h-5" />
                  <div>
                    <p className="font-medium">Rejected</p>
                    {user.identity_verification_notes && (
                      <p className="text-xs text-red-600">Note: {user.identity_verification_notes}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-500">
                  This VA has not submitted identity verification yet.
                </div>
              )}
            </div>
          )}

          {/* QR Image Preview Modal */}
          {showQRPreview && user.identity_verification_qr_link && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]" onClick={() => setShowQRPreview(false)}>
              <div className="bg-white rounded-lg p-4 max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">QR Code Image</h4>
                  <button onClick={() => setShowQRPreview(false)} className="p-1 hover:bg-gray-100 rounded">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-amber-600 mb-2">⚠ This image is loaded from an external user-supplied URL.</p>
                <img
                  src={user.identity_verification_qr_link}
                  alt="Verification QR Code"
                  className="w-full rounded-lg border"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
          )}

          {/* Moderation History Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Warnings */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-yellow-900 mb-4 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2" />
                Warnings
              </h3>
              
              {loadingModeration ? (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-yellow-500"></div>
                </div>
              ) : warnings.length === 0 ? (
                <div className="text-center py-6 text-yellow-600">
                  <p className="font-medium">No warnings</p>
                  <p className="text-sm">This user has no warnings.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-2xl font-bold text-yellow-700 mb-4">{warnings.length}</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {warnings.map((warning) => (
                      <div key={warning.id} className="text-sm bg-white rounded p-2 border border-yellow-100">
                        <p className="font-medium text-gray-900">{warning.reason || 'User Warning'}</p>
                        <p className="text-xs text-gray-500 mt-1">{new Date(warning.created_at).toLocaleDateString()} at {new Date(warning.created_at).toLocaleTimeString()}</p>
                        {warning.admin?.full_name && (
                          <p className="text-xs text-gray-500">By: {warning.admin.full_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Suspensions */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-red-900 mb-4 flex items-center">
                <Ban className="w-5 h-5 mr-2" />
                Suspensions
              </h3>
              
              {loadingModeration ? (
                <div className="flex justify-center items-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-red-500"></div>
                </div>
              ) : suspensions.length === 0 ? (
                <div className="text-center py-6 text-red-600">
                  <p className="font-medium">No suspensions</p>
                  <p className="text-sm">This user has not been suspended.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-2xl font-bold text-red-700 mb-4">{suspensions.length}</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {suspensions.map((suspension) => (
                      <div key={suspension.id} className="text-sm bg-white rounded p-2 border border-red-100">
                        <p className="font-medium text-gray-900">{suspension.reason || 'User Suspension'}</p>
                        <p className="text-xs text-gray-500 mt-1">{new Date(suspension.created_at).toLocaleDateString()} at {new Date(suspension.created_at).toLocaleTimeString()}</p>
                        {suspension.admin?.full_name && (
                          <p className="text-xs text-gray-500">By: {suspension.admin.full_name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Reports History */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-gray-600" />
              Report History
              {reports.length > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                  {reports.length}
                </span>
              )}
            </h3>
            
            {loadingReports ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : reportsError ? (
              <div className="text-center py-4 text-red-500">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                <p>{reportsError}</p>
              </div>
            ) : reports.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="font-medium">No reports found</p>
                <p className="text-sm">This user has not been reported.</p>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-3 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
                {reports.map((report) => (
                  <div key={report.id} className="bg-gray-50 rounded-lg p-4 border-l-4 border-l-red-400">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900 capitalize">{report.type} Report</span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            report.status === 'resolved' ? 'bg-green-100 text-green-800' :
                            report.status === 'dismissed' ? 'bg-gray-100 text-gray-800' :
                            report.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {report.status?.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            report.priority === 'critical' ? 'bg-red-100 text-red-800' :
                            report.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            report.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {report.priority?.toUpperCase()}
                          </span>
                        </div>
                        <p className="font-medium text-sm text-gray-800 mt-1">{report.reason}</p>
                        {report.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{report.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-4">
                        <span>Reported by: {report.reporter?.full_name || 'Anonymous'}</span>
                        <span>{new Date(report.created_at).toLocaleDateString()}</span>
                      </div>
                      {report.resolved_at && (
                        <span>Resolved: {new Date(report.resolved_at).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 border-b pb-2 mb-4 flex items-center">
              <Activity className="w-5 h-5 mr-2 text-gray-600" />
              Recent Activity
              {activities.length > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {activities.length}
                </span>
              )}
            </h3>
            
            {loadingActivities ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : activitiesError ? (
              <div className="text-center py-4 text-red-500">
                <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                <p>{activitiesError}</p>
              </div>
            ) : activities.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Activity className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="font-medium">No recent activity</p>
                <p className="text-sm">No activities found for this user.</p>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-3 pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
                {activities.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        activity.type === 'admin_action' ? 'bg-red-100' : 
                        activity.type === 'user_activity' ? 'bg-blue-100' : 'bg-gray-100'
                      }`}>
                        {activity.type === 'admin_action' ? (
                          <Shield className={`w-4 h-4 ${
                            activity.type === 'admin_action' ? 'text-red-600' : 'text-gray-600'
                          }`} />
                        ) : activity.metadata?.actionType?.includes('message') || activity.title.toLowerCase().includes('message') ? (
                          <Mail className="w-4 h-4 text-blue-600" />
                        ) : activity.title.toLowerCase().includes('application') ? (
                          <FileText className="w-4 h-4 text-blue-600" />
                        ) : activity.title.toLowerCase().includes('job') ? (
                          <ExternalLink className="w-4 h-4 text-blue-600" />
                        ) : activity.title.toLowerCase().includes('profile') || activity.title.toLowerCase().includes('view') ? (
                          <Eye className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Activity className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-sm text-gray-900 line-clamp-1">
                            {activity.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {activity.description}
                          </p>
                          {activity.metadata?.admin && (
                            <p className="text-xs text-gray-500 mt-1">
                              By: {activity.metadata.admin}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 ml-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            activity.type === 'admin_action' ? 'bg-red-100 text-red-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {activity.type === 'admin_action' ? 'Admin' : 'User'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500">
                          {formatTimeAgo(activity.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Moderation Actions</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Action Notes</label>
                <textarea
                  value={actionNote}
                  onChange={(e) => setActionNote(e.target.value)}
                  placeholder="Add notes about your action..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-3">
                {(user.moderation_status?.status || 'active') === 'active' ? (
                  <button
                    onClick={() => handleAction('suspend')}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <UserX className="w-4 h-4" />
                    <span>Suspend User</span>
                  </button>
                ) : (
                  <button
                    onClick={() => handleAction('activate')}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <UserCheck className="w-4 h-4" />
                    <span>Activate User</span>
                  </button>
                )}
                
                <button
                  onClick={() => handleAction('warn')}
                  className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  <AlertTriangle className="w-4 h-4" />
                  <span>Send Warning</span>
                </button>

                <button
                  onClick={() => handleAction('ban')}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Ban className="w-4 h-4" />
                  <span>Permanent Ban</span>
                </button>

                {userProfile?.role === 'admin' && (
                  <button
                    onClick={() => setShowDeleteConfirmation(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-900 text-white rounded-lg hover:bg-red-950 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete User</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteUserConfirmationDialog
        isOpen={showDeleteConfirmation}
        user={user}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setShowDeleteConfirmation(false)}
      />

      {/* Suspension Modal */}
      <SuspensionModal
        isOpen={showSuspensionModal}
        user={user}
        onConfirm={handleSuspensionConfirm}
        onCancel={() => setShowSuspensionModal(false)}
      />
    </div>
  );
};

export default UserDetailsModal;
